import type * as ngCore from '@angular/core'
import type * as ents from '@attain-apps/entities'
import * as ds from '@attain-apps/fe/web/main/lib/design'
import * as ec from '@attain-apps/fe/web/main/lib/entity-collection'
import type * as rxjs from 'rxjs'

import { Api } from './api.service'
import type { Feature } from './feature'
import { Name } from './name'
import * as plugins from './plugins'
import type { TypeParams } from './types'

/**
 * Entity-scoped Organization collection.
 *
 * Each instance owns Organization Records for exactly one Organization Entity.
 */
export class Collection extends ec.Collection<TypeParams> {
  /**
   * The Organization Entity ID whose Records this Collection manages
   */
  public readonly entityId: ents.core.dms.models.Entity.Id

  constructor(
    config: Collection.Config,
    injectableDependencies: ec.InjectableDependencies,
  ) {
    const {
      canEditJointUseSg,
      entityId,
      organizationsFeature,
      scenarioContext$,
    } = config
    super(
      {
        api: new Api({
          entityId,
          organizationsFeature,
        }),
        context$: scenarioContext$,
        identify: organization => organization.id,
        name: Name.from(entityId),
        plugins: {
          additionalFactories: [
            new plugins.jointUse.Factory(),
            new ds.table.ec.Factory<TypeParams>(collection => ({
              rowConfig: ({ entity: { id } }) => {
                const canEditJointUse = canEditJointUseSg()

                return {
                  iconsConfigs: canEditJointUse
                    ? [
                        {
                          class: 'atn-icon atn-edit atn-blue',
                          onClick: () =>
                            collection.plugins.editingPlugin.feature.actions.edit
                              .from(`Table.Row-${id}.Icons.Edit.Clicked`)
                              .dispatch({
                                id,
                                makeCurrent: {
                                  target:
                                    plugins.jointUse.CURRENT_ENTITY_TARGET,
                                },
                              }),
                        },
                      ]
                    : [],
                }
              },
            })),
            new ds.input.select.singleEc.ec.Factory<TypeParams>({
              getItem: ({ name, id }) => ({
                label: name,
                value: id,
              }),
            }),
          ],
        },
      },
      injectableDependencies,
    )
    this.entityId = entityId
  }
}

export namespace Collection {
  export interface Config {
    /** Whether the current user can edit Joint Use (admins only) */
    canEditJointUseSg: ngCore.Signal<boolean>
    entityId: ents.core.dms.models.Entity.Id
    organizationsFeature: Feature
    scenarioContext$: rxjs.Observable<
      ents.core.admin.Context.ForScenario | undefined
    >
  }
}
